import * as React from 'react';
import dayjs from 'dayjs';
import { DateRangeValue, message } from '@traceair/tds';
import { ScanCostWithDateType } from '../types';
import getPilotApiService from '../../../../api_services/PilotApiService';
import Logger from '../../../../libs/logger/Logger';

const TIMEOUT_MS = 30000;

export function unpaidMissionsRange(costs: ScanCostWithDateType[]): [dayjs.Dayjs, dayjs.Dayjs] {
  const billable = costs.filter((c) => !c.isFailed);
  if (billable.length === 0) return [dayjs(), dayjs()];
  const timestamps = billable.map((c) => c.flightDate.getTime());
  return [dayjs(new Date(Math.min(...timestamps))), dayjs(new Date(Math.max(...timestamps)))];
}

function fourteenDays(): [dayjs.Dayjs, dayjs.Dayjs] {
  return [dayjs().subtract(13, 'day'), dayjs()];
}

// Header shown above the bulleted list of records pilot-api dropped under warnings.missingDate.
const OPS_CONTACT_HEADER = 'Please contact OPS to recheck data inconsistency — the following record(s) lack a date:';

// One block element per line: the TDS message description collapses `\n` (white-space: normal).
function buildMissingDateToastDescription(descriptions: string[]): React.ReactNode {
  return React.createElement(
    'div',
    null,
    React.createElement('div', null, OPS_CONTACT_HEADER),
    ...descriptions.map((d, i) => React.createElement('div', { key: i }, `• ${d}`)),
  );
}

export function useScanCosts() {
  const isMount = React.useRef(true);
  const [scanCosts, setScanCosts] = React.useState<ScanCostWithDateType[] | null>(null);
  const [scanCostsFetchError, setScanCostsFetchError] = React.useState(false);
  const [isFixedFee, setIsFixedFee] = React.useState(true);
  const [dateRange, setDateRange] = React.useState<DateRangeValue | undefined>(undefined);
  const [savedBankDetails, setSavedBankDetails] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    async function fetch() {
      const timer = setTimeout(() => {
        if (isMount.current) setScanCostsFetchError(true);
      }, TIMEOUT_MS);
      try {
        const { costs, isFixedFeePaymentType, bankDetails, warnings } = await getPilotApiService().getPilotCosts();
        const costsWithDate = costs.map((r) => {
          const raw = r as unknown as Record<string, unknown>;
          return {
            ...r,
            flightDate: new Date(`${r.flightDate}T00:00`),
            itemCount: (raw.items as number | undefined) ?? r.itemCount ?? 0,
            account: r.account ?? '',
            unprocessed: (raw.isUnprocessed as boolean | undefined) ?? r.unprocessed ?? false,
            isFailed: r.isFailed ?? false,
          };
        });
        clearTimeout(timer);
        if (isMount.current) {
          setScanCosts(costsWithDate);
          setIsFixedFee(isFixedFeePaymentType);
          setSavedBankDetails(bankDetails);
          setScanCostsFetchError(false);
          const hasUnpaid = costsWithDate.some((c) => !c.isFailed);
          setDateRange(hasUnpaid ? unpaidMissionsRange(costsWithDate) : fourteenDays());
          // pilot-api dropped some records that OPS left dateless. Costs still render; the
          // toast names the exact bad record(s) so the pilot can forward it to OPS without
          // getting stuck. Not treated as an error — the fetch succeeded for the billable set.
          if (warnings?.missingDate?.length) {
            message.error({
              title: 'Some records are missing a date',
              description: buildMissingDateToastDescription(warnings.missingDate),
              closable: true,
              // Sticky until closed — the pilot needs time to copy the records and forward them to OPS.
              duration: null,
              theme: 'dark',
            });
          }
        }
      } catch (err) {
        clearTimeout(timer);
        Logger.error(err as Error | string);
        if (isMount.current) setScanCostsFetchError(true);
      }
    }
    fetch();
    return () => {
      isMount.current = false;
    };
  }, []);

  return { scanCosts, scanCostsFetchError, isFixedFee, dateRange, setDateRange, savedBankDetails };
}
